import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Hotel, Plane, Headphones, ShoppingBag, AlertTriangle, Package, PackageX, PackageOpen, Clock, CreditCard, CalendarClock, CalendarCheck, ShoppingCart, Receipt, CircleX, GitCompare, Phone, SpellCheck, Ban, ListChecks, MessageSquare, Sparkles, Briefcase, LayoutGrid } from "lucide-react";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { listScenarios, listCustomScenarios, deleteCustomScenario, getHome } from "@/lib/api";
import { levelLabel } from "@/lib/labels";
import { toast } from "sonner";

const ICONS = { Hotel, Plane, Headphones, ShoppingBag, AlertTriangle, Package, PackageX, PackageOpen, Clock, CreditCard, CalendarClock, CalendarCheck, ShoppingCart, Receipt, CircleX, GitCompare, PhonePaused: Phone, SpellCheck, Ban, ListChecks, MessageSquare, Sparkles };

// Industries with zero curated matches — for those we send learners back to
// the prefilled custom-practice fallback on Home, not an empty catalog.
// Note: hospital_cleaning and office both received 5/5/5 curated coverage in
// Wave 2, so they are no longer in this set and are filtered normally.
const NO_CATALOG_INDUSTRIES = new Set(["other"]);

export default function Practice() {
  const { lang, tr } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [curated, setCurated] = useState([]);
  const [custom, setCustom] = useState([]);
  const [q, setQ] = useState("");
  const initialLevel = ["basic", "intermediate", "advanced"].includes(searchParams.get("level"))
    ? searchParams.get("level")
    : "all";
  const [diff, setDiff] = useState(initialLevel);
  const [loading, setLoading] = useState(true);
  const [homeCtx, setHomeCtx] = useState(null);

  const savedIndustry = user?.work_profile?.industry || user?.work_context || null;
  // "browse=all" query param opts-out of the job filter for THIS visit only.
  // It does not change the saved profile.
  const browseAll = searchParams.get("browse") === "all";
  const isRemixMode = searchParams.get("mode") === "remix";
  const shouldFilter = !!savedIndustry && !NO_CATALOG_INDUSTRIES.has(savedIndustry) && !browseAll;
  const effectiveIndustry = shouldFilter ? savedIndustry : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [sc, cus, home] = await Promise.all([
          listScenarios(effectiveIndustry || undefined),
          listCustomScenarios().catch(() => []),
          user ? getHome().catch(() => null) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setCurated(sc);
        setCustom(cus);
        setHomeCtx(home);
      } catch (err) {
        toast.error(tr("errorGeneric"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [effectiveIndustry, user?.id]);

  const filtered = useMemo(() => {
    // Remix mode: restrict to curated scenarios the learner has already
    // finished at the currently-selected level (from /api/home
    // core_progress.completed_ids). Search + level chip still apply.
    const remixSet = isRemixMode
      ? new Set(homeCtx?.core_progress?.completed_ids || [])
      : null;
    return curated.filter((s) => {
      const matchQ = !q || [s.title_es, s.title_en, s.category_es, s.category_en].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()));
      const matchDiff = diff === "all" || s.difficulty === diff;
      const matchRemix = !remixSet || remixSet.has(s.id);
      return matchQ && matchDiff && matchRemix;
    });
  }, [curated, q, diff, isRemixMode, homeCtx]);

  const doDeleteCustom = async (id) => {
    try {
      await deleteCustomScenario(id.replace("custom:", ""));
      setCustom((prev) => prev.filter((c) => c.id !== id));
    } catch (err) { /* ignore */ }
  };

  const industryI18nKey = {
    retail: "industryRetail",
    hospital_cleaning: "industryHospitalClean",
    hospitality: "industryHospitality",
    restaurant: "industryRestaurant",
    call_center: "industryCallCenter",
    travel: "industryTravel",
    office: "industryOffice",
    other: "industryOther",
  }[savedIndustry || ""];
  const industryLabel = shouldFilter
    ? (lang === "en" ? homeCtx?.industry_label_en : homeCtx?.industry_label_es)
      || (industryI18nKey ? tr(industryI18nKey) : savedIndustry)
    : null;

  const noCatalogMatchForSavedIndustry = savedIndustry && NO_CATALOG_INDUSTRIES.has(savedIndustry);

  return (
    <AppShell>
      <section className="px-5 pt-4 pb-8 space-y-4" data-testid="practice-screen">
        <h1 className="text-[22px] font-bold text-[rgb(var(--wpe-navy))]" data-testid="practice-title">
          {isRemixMode ? tr("practiceRemixTitle") : tr("dashboardTitle")}
        </h1>
        <p className="text-[12px] text-[rgb(var(--wpe-slate))] -mt-2" data-testid="practice-choice-explain">
          {isRemixMode ? tr("practiceRemixSubtitle") : tr("practiceChoiceExplain")}
        </p>

        {/* Custom-create CTA — always visible so every job can practice their own scenario, alongside curated matches. Reuses the existing /create entry point. */}
        <button
          type="button"
          onClick={() => navigate("/create")}
          className="wpe-card w-full text-left flex items-start gap-3"
          data-testid="practice-create-cta"
        >
          <span className="w-10 h-10 rounded-full bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] flex items-center justify-center shrink-0">
            <Sparkles size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))]">
              {tr("createFromPractice")}
            </div>
            <div className="text-[11px] text-[rgb(var(--wpe-muted))]">
              {tr("createFromPracticeSub")}
            </div>
          </div>
        </button>

        {/* Scope chip: shows the current filter mode and the toggle to browse all */}
        <div
          className="wpe-card flex items-start justify-between gap-3"
          data-testid="practice-scope-chip"
        >
          <div className="flex items-start gap-2 min-w-0">
            <span className="w-8 h-8 rounded-full bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] flex items-center justify-center shrink-0">
              {shouldFilter ? <Briefcase size={14} /> : <LayoutGrid size={14} />}
            </span>
            <div className="min-w-0">
              <div className="text-[12px] font-semibold text-[rgb(var(--wpe-navy))]">
                {shouldFilter
                  ? tr("practiceScopeForMyJob").replace("{job}", industryLabel)
                  : tr("practiceScopeAllAreas")}
              </div>
              <div className="text-[11px] text-[rgb(var(--wpe-muted))]" data-testid="practice-scope-hint">
                {shouldFilter ? tr("practiceScopeForMyJobHint") : tr("practiceScopeAllAreasHint")}
              </div>
            </div>
          </div>
          {shouldFilter ? (
            <button
              type="button"
              onClick={() => setSearchParams({ browse: "all" })}
              className="wpe-ghost text-[11px] shrink-0"
              data-testid="practice-browse-all-btn"
            >
              {tr("practiceBrowseAll")}
            </button>
          ) : savedIndustry && !NO_CATALOG_INDUSTRIES.has(savedIndustry) ? (
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className="wpe-ghost text-[11px] shrink-0"
              data-testid="practice-back-to-job-btn"
            >
              {tr("practiceBackToMyJob")}
            </button>
          ) : null}
        </div>

        {/* Empty-catalog fallback for hospital_cleaning / office / other with a description */}
        {noCatalogMatchForSavedIndustry && !browseAll && (
          <div className="wpe-hint text-[12px]" data-testid="practice-no-catalog-note">
            {tr("practiceNoCatalog")}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => navigate("/home")}
                className="wpe-cta text-[12px] flex-1"
                data-testid="practice-open-personalized-btn"
              >
                {tr("practiceOpenPersonalized")}
              </button>
              <button
                type="button"
                onClick={() => setSearchParams({ browse: "all" })}
                className="wpe-secondary text-[12px] flex-1"
                data-testid="practice-browse-all-btn-fallback"
              >
                {tr("practiceBrowseAll")}
              </button>
            </div>
          </div>
        )}

        <div className="wpe-card flex items-center gap-2 py-2">
          <Search size={14} className="text-[rgb(var(--wpe-muted))]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tr("searchPlaceholder")}
            className="flex-1 bg-transparent border-0 focus:outline-none text-[13px] text-[rgb(var(--wpe-navy))]"
            data-testid="practice-search"
          />
        </div>

        <div className="flex gap-2 flex-wrap" data-testid="difficulty-filters">
          {["all", "basic", "intermediate", "advanced"].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDiff(d)}
              className={`text-[11px] px-3 py-1 rounded-full border ${diff === d ? "bg-[rgb(var(--wpe-teal))] text-white border-[rgb(var(--wpe-teal))]" : "bg-white text-[rgb(var(--wpe-slate))] border-[rgb(var(--wpe-border))]"}`}
              data-testid={`filter-${d}`}
            >
              {d === "all" ? tr("filterAll") : d === "basic" ? tr("levelBasic") : d === "intermediate" ? tr("levelInter") : tr("levelAdvanced")}
            </button>
          ))}
        </div>

        {!isRemixMode && custom.length > 0 && (
          <div className="space-y-2" data-testid="custom-list">
            <h2 className="wpe-eyebrow">{tr("aiCreatedLabel")}</h2>
            {custom.map((c) => (
              <div key={c.id} className="wpe-scenario-card" data-testid={`custom-card-${c.id}`}>
                <span className="w-10 h-10 rounded-full bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))] flex items-center justify-center shrink-0">
                  <Sparkles size={18} />
                </span>
                <button className="flex-1 min-w-0 text-left" onClick={() => navigate(`/briefing/${c.id}`)}>
                  <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug">
                    {lang === "en" ? c.scenario.title_en : c.scenario.title_es}
                  </div>
                  <div className="text-[11px] text-[rgb(var(--wpe-muted))]">
                    {c.scenario.difficulty ? levelLabel(lang, c.scenario.difficulty) : ""} · {c.scenario.time_min}m · {tr("contentGenerated")}
                  </div>
                </button>
                <button className="wpe-ghost text-[11px]" onClick={() => doDeleteCustom(c.id)}>
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3" data-testid="curated-list">
          {loading && <div className="wpe-card animate-pulse text-[rgb(var(--wpe-muted))] text-sm">…</div>}
          {!loading && isRemixMode && filtered.length === 0 && (
            <div className="wpe-hint text-[12px]" data-testid="practice-remix-empty">
              {tr("practiceRemixEmpty")}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setSearchParams({})}
                  className="wpe-secondary text-[12px]"
                  data-testid="practice-remix-back-btn"
                >
                  {tr("practiceRemixBackToCatalog")}
                </button>
              </div>
            </div>
          )}
          {filtered.map((s, idx) => {
            const Icon = ICONS[s.icon] || MessageSquare;
            const accents = ["coral", "gold", "blue", "teal", "lavender"];
            const accent = accents[idx % accents.length];
            const chipClass = { teal: "wpe-chip-teal", coral: "wpe-chip-coral", gold: "wpe-chip-gold", blue: "wpe-chip-blue", lavender: "wpe-chip-lavender" }[accent];
            const eyebrowClass = { teal: "wpe-eyebrow-teal", coral: "wpe-eyebrow-coral", gold: "wpe-eyebrow-gold", blue: "wpe-eyebrow-blue", lavender: "wpe-eyebrow-lavender" }[accent];
            return (
              <button
                key={s.id}
                type="button"
                className="wpe-scenario-card-tinted"
                onClick={() =>
                  navigate(isRemixMode ? `/briefing/${s.id}?mode=remix` : `/briefing/${s.id}`)
                }
                data-testid={`scenario-card-${s.id}`}
              >
                <span className={`w-12 h-12 rounded-2xl ${chipClass} flex items-center justify-center shrink-0`}>
                  <Icon size={22} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`${eyebrowClass} mb-0.5`}>
                    {lang === "en" ? s.category_en : s.category_es}
                  </div>
                  <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug">
                    {lang === "en" ? s.title_en : s.title_es}
                  </div>
                  <div className="text-[11px] text-[rgb(var(--wpe-muted))] mt-1">
                    {({ basic: tr("levelBasic"), intermediate: tr("levelInter"), advanced: tr("levelAdvanced") })[s.difficulty] || s.difficulty} · {s.time_min}m
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <PilotDisclaimer />
      </section>
    </AppShell>
  );
}
